import React, { useState } from 'react';
import { Flame, Menu, X } from 'lucide-react';
import QuizModal from './quiz/QuizModal';

export default function LandingPage({ onGetStarted }: { onGetStarted: () => void }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-midnight-900 via-midnight-800 to-midnight-900">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-midnight-900/80 backdrop-blur-md z-50 border-b border-midnight-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <Flame className="h-8 w-8 text-amber-500" />
              <span className="text-2xl font-bold bg-gradient-to-r from-amber-400 to-marigold-400 bg-clip-text text-transparent">
                Shadow Worker℠
              </span>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="mailto:brooks@fireengine.co" className="text-midnight-100 hover:text-amber-400 transition-colors">Contact</a>
              <button
                onClick={onGetStarted}
                className="px-6 py-3 bg-gradient-to-r from-amber-500 to-marigold-500 text-white rounded-lg hover:from-amber-400 hover:to-marigold-400 transition-all shadow-lg shadow-midnight-900/50"
              >
                Sign In
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 hover:bg-midnight-800 rounded-lg transition-colors text-midnight-100"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="fixed inset-0 bg-midnight-900/90 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="fixed top-20 inset-x-0 bg-midnight-800 border-b border-midnight-700">
            <div className="p-4">
              <nav className="space-y-2">
                <a href="mailto:brooks@fireengine.co" className="block px-4 py-2 text-midnight-100 hover:bg-midnight-700 rounded-lg">
                  Contact
                </a>
              </nav>
              <div className="mt-4 pt-4 border-t border-midnight-700">
                <button
                  onClick={() => {
                    onGetStarted();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-marigold-500 text-white rounded-lg hover:from-amber-400 hover:to-marigold-400 transition-all shadow-lg shadow-midnight-900/50"
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="pt-28 sm:pt-32 md:pt-40 lg:pt-48">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center mb-20">            
            <h1 className="text-5xl sm:text-5xl md:text-7xl font-bold text-midnight-50 mb-8 leading-tight">
              {/* Mobile Layout */}
              <div className="block md:hidden">
                <span className="block">Your Inner</span>
                <span className="block">Journey, Guided,</span>
                <span className="block bg-gradient-to-r from-amber-400 to-marigold-400 bg-clip-text text-transparent">Illuminated</span>
                <span className="block">& Organized</span>
              </div>
              {/* Desktop Layout */}
              <div className="hidden md:block">
                Your Inner Journey,{'\n'}
                <span className="inline-block">
                  <span>Guided, </span>
                  <span className="bg-gradient-to-r from-amber-400 to-marigold-400 bg-clip-text text-transparent">Illuminated</span>
                  <span> & Organized</span>
                </span>
              </div>
            </h1>
            
            <p className="text-lg sm:text-xl text-midnight-300 mb-12 max-w-2xl mx-auto leading-relaxed">
              With an{' '}
              <span className="bg-gradient-to-r from-amber-400 to-marigold-400 bg-clip-text text-transparent">
                AI shadow work therapist
              </span>
              , journaling tools, and part mapping, this app helps you document, visualize, and track your parts work – all in one place.
            </p>

            <div className="flex justify-center">
              <button
                onClick={() => setShowQuiz(true)}
                className="px-8 py-4 bg-gradient-to-r from-amber-500 to-marigold-500 text-white rounded-lg hover:from-amber-400 hover:to-marigold-400 transition-all shadow-lg shadow-midnight-900/50 text-lg font-medium"
              >
                Take the Quiz
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="py-8 border-t border-midnight-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <Flame className="h-6 w-6 text-amber-500" />
              <span className="text-midnight-100">Shadow Worker℠</span>
            </div>
            <p className="text-midnight-400 text-sm">
              © {new Date().getFullYear()} Shadow Worker. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Quiz Modal */}
      {showQuiz && (
        <QuizModal
          onClose={() => setShowQuiz(false)}
          onComplete={onGetStarted}
        />
      )}
    </div>
  );
}